import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Avatar
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import api from '../../../utils/api';

function Step7Logo({ formData, businessId, onComplete, onError }) {
  const [error, setError] = useState('');
  const [logoUrl, setLogoUrl] = useState(formData.logoUrl || '');
  const [uploading, setUploading] = useState(false);
  const [logoAssociated, setLogoAssociated] = useState(false);

  // Auto-associate logo with business if it was scraped from website
  React.useEffect(() => {
    if (formData.logoUrl && businessId && !logoAssociated) {
      // Logo was already processed and uploaded to S3 during scraping
      setLogoUrl(formData.logoUrl);
      
      // Update business logo via API (logoUrl is already an S3 URL from scraping)
      const associateLogo = async () => {
        try {
          await api.put(`/api/supplier/business/${businessId}/logo`, {
            logoUrl: formData.logoUrl
          }, {
            headers: {
              'Content-Type': 'application/json'
            }
          });
          setLogoAssociated(true);
        } catch (err) {
          // Continue anyway - logo URL is already set
          setLogoAssociated(true);
        }
      };
      
      associateLogo();
    }
  }, [formData.logoUrl, businessId, logoAssociated]);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type - only JPEG and PNG allowed
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only JPEG and PNG files are allowed. Please select a JPEG or PNG image.');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      setError('File size must be less than 2MB');
      return;
    }

    if (!businessId) {
      setError('Business ID is required. Please complete previous steps first.');
      return;
    }

    try {
      setUploading(true);
      setError('');

      const formData = new FormData();
      formData.append('logo', file);

      const response = await api.put(
        `/api/supplier/business/${businessId}/logo`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setLogoUrl(response.data.logoUrl);
      
      // Pass the updated formData with logoUrl, not the file upload FormData
      onComplete({
        logoUrl: response.data.logoUrl
      });
    } catch (err) {
      setError('Try a different file or skip this step.');
      if (onError) onError(err);
    } finally {
      setUploading(false);
    }
  };

  const handleSkip = () => {
    onComplete({
      ...formData
    });
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Add Your Logo
      </Typography>
      <Typography variant="body2" sx={{ mb: 3 }}>
        Add your logo to help couples recognize your brand. Full portfolio can be added later in your profile.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ textAlign: 'center', py: 4 }}>
        {logoUrl || formData.logoUrl ? (
          <Box>
            <Avatar
              src={logoUrl || formData.logoUrl}
              alt="Logo"
              sx={{
                width: 200,
                height: 200,
                mx: 'auto',
                mb: 2,
                border: '2px solid',
                borderColor: 'divider',
                bgcolor: 'white'
              }}
            />
            <Typography variant="body2" sx={{ mb: 2 }}>
              {formData.logoUrl && !logoUrl ? 'Logo found from your website' : 'Logo uploaded successfully'}
            </Typography>
            <Button
              variant="outlined"
              component="label"
              disabled={uploading}
            >
              {uploading ? <CircularProgress size={24} /> : 'Change Logo'}
              <input
                type="file"
                hidden
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleFileSelect}
              />
            </Button>
          </Box>
        ) : (
          <Box>
            <Button
              variant="contained"
              component="label"
              startIcon={<CloudUpload />}
              disabled={uploading || !businessId}
              sx={{ mb: 2 }}
            >
              {uploading ? <CircularProgress size={24} /> : 'Upload Logo'}
              <input
                type="file"
                hidden
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleFileSelect}
              />
            </Button>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
              Max file size: 2MB. Only JPEG and PNG files are supported.
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
        <Button
          variant="outlined"
          onClick={handleSkip}
          disabled={uploading}
        >
          Skip for Now
        </Button>
        {(logoUrl || formData.logoUrl) && (
          <Button
            variant="contained"
            onClick={() => onComplete({ ...formData, logoUrl: logoUrl || formData.logoUrl })}
            disabled={uploading}
          >
            Continue
          </Button>
        )}
      </Box>
    </Box>
  );
}

export default Step7Logo;

